import { useEffect, useMemo, useState } from 'react';
import type { HTMLAttributes } from 'react';
import {
	Button,
	Card,
	Pagination,
	Space,
	Table,
	Tag,
	Tooltip,
	Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { HistoryOutlined } from '@ant-design/icons';

import type {
	PendingCutBar,
	PendingCutMaterialGroup,
} from '../../../../redux/api/pending-cuts';
import type { LocationLabelFormatter } from '../../../../utils/locationLabel';
import {
	PendingCutRow,
	barLocationText,
	dueText,
	groupCounts,
	inches,
	jobText,
	paginateGroupsByBars,
	piecesDetail,
	reservationCount,
	statusText,
	toRows,
} from './rows';

const { Text } = Typography;

const COLUMN_COUNT = 9;
const DEFAULT_PAGE_SIZE = 100;
const PAGE_SIZE_OPTIONS = ['50', '100', '200'];

const Flags = ({ flags }: { flags: string[] }) =>
	flags.length ? (
		<Space size={[4, 4]} wrap>
			{flags.map((flag) => (
				<Tag key={flag} color="warning">
					{flag}
				</Tag>
			))}
		</Space>
	) : null;

const buildColumns = (
	formatLocation: LocationLabelFormatter,
	onOpenHistory: (bar: PendingCutBar) => void,
	showBuilding: boolean
): ColumnsType<PendingCutRow> => {
	const barCell = (row: PendingCutRow) => ({
		rowSpan: row.kind === 'reservation' ? row.barSpan : 1,
		colSpan: row.kind === 'section' ? 0 : 1,
	});
	const reservationCell = (row: PendingCutRow) => ({
		colSpan: row.kind === 'section' ? 0 : 1,
	});
	return [
		{
			title: 'Bar',
			key: 'bar',
			width: 130,
			onCell: (row) =>
				row.kind === 'section'
					? { colSpan: COLUMN_COUNT }
					: { rowSpan: row.barSpan },
			render: (_: unknown, row) => {
				if (row.kind === 'section') {
					return (
						<span>
							<Text strong>
								{row.group.material_name}
								{row.group.continued ? ' (continued)' : ''}
							</Text>
							<Text type="secondary"> · {groupCounts(row.group)}</Text>
						</span>
					);
				}
				return (
					<span className="whitespace-nowrap">
						<Text strong className="font-mono">
							{row.bar.friendly_id ?? '—'}
						</Text>
						<Tooltip title="Full history">
							<Button
								size="small"
								type="text"
								icon={<HistoryOutlined />}
								aria-label={`Full history of ${row.bar.friendly_id}`}
								onClick={() => onOpenHistory(row.bar)}
							/>
						</Tooltip>
					</span>
				);
			},
		},
		{
			title: 'Location',
			key: 'location',
			width: 200,
			onCell: barCell,
			render: (_: unknown, row) => {
				if (row.kind !== 'reservation') return null;
				const label = barLocationText(row.bar, formatLocation, showBuilding);
				return label ? label : <Text type="danger">No location</Text>;
			},
		},
		{
			title: 'Bar length',
			key: 'length',
			width: 170,
			onCell: barCell,
			render: (_: unknown, row) =>
				row.kind === 'reservation' && (
					<div className="whitespace-nowrap">
						<div>{inches(row.bar.current_length)}</div>
						<Text
							type={row.bar.is_short ? 'danger' : 'secondary'}
							className="text-xs"
						>
							{inches(row.bar.remaining_after_cuts)} left after cuts
						</Text>
					</div>
				),
		},
		{
			title: 'Flags',
			key: 'flags',
			width: 260,
			onCell: reservationCell,
			render: (_: unknown, row) =>
				row.kind === 'reservation' && (
					<Flags
						flags={[
							...(row.barSpan ? row.bar.flags : []),
							...row.reservation.flags,
						]}
					/>
				),
		},
		{
			title: 'Cut length',
			key: 'cut',
			width: 120,
			onCell: reservationCell,
			render: (_: unknown, row) =>
				row.kind === 'reservation' && (
					<div className="whitespace-nowrap">
						<div>{inches(row.reservation.cut_length)}</div>
						<Text type="secondary" className="text-xs">
							kerf {inches(row.reservation.saw_kerf)}
						</Text>
					</div>
				),
		},
		{
			title: 'Pcs to cut',
			key: 'pieces',
			width: 190,
			onCell: reservationCell,
			render: (_: unknown, row) =>
				row.kind === 'reservation' && (
					<div>
						<Text strong>{row.reservation.pending_quantity}</Text>
						<div>
							<Text type="secondary" className="text-xs">
								{piecesDetail(row.reservation)}
							</Text>
						</div>
					</div>
				),
		},
		{
			title: 'Job',
			key: 'job',
			width: 180,
			onCell: reservationCell,
			render: (_: unknown, row) =>
				row.kind === 'reservation' && (
					<Text ellipsis={{ tooltip: jobText(row.reservation) }}>
						{jobText(row.reservation)}
					</Text>
				),
		},
		{
			title: 'Due',
			key: 'due',
			width: 90,
			onCell: reservationCell,
			render: (_: unknown, row) =>
				row.kind === 'reservation' && dueText(row.reservation),
		},
		{
			title: 'Status',
			key: 'status',
			width: 140,
			onCell: reservationCell,
			render: (_: unknown, row) =>
				row.kind === 'reservation' && statusText(row.reservation),
		},
	];
};

interface PendingCutsTableProps {
	groups: PendingCutMaterialGroup[];
	formatLocation: LocationLabelFormatter;
	onOpenHistory: (bar: PendingCutBar) => void;
	showBuilding: boolean;
	/** Override for tests so the pager can run on a small fixture. */
	initialPageSize?: number;
	pageSizeOptions?: string[];
}

const PendingCutsTable = ({
	groups,
	formatLocation,
	onOpenHistory,
	showBuilding,
	initialPageSize = DEFAULT_PAGE_SIZE,
	pageSizeOptions = PAGE_SIZE_OPTIONS,
}: PendingCutsTableProps) => {
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(initialPageSize);

	useEffect(() => {
		setPage(1);
	}, [groups]);

	const pages = useMemo(
		() => paginateGroupsByBars(groups, pageSize),
		[groups, pageSize]
	);
	const safePage = Math.min(page, Math.max(pages.length, 1));
	const pageGroups = pages[safePage - 1] ?? [];
	const rows = useMemo(() => toRows(pageGroups), [pageGroups]);
	const totalReservations = useMemo(() => reservationCount(groups), [groups]);
	const columns = useMemo(
		() => buildColumns(formatLocation, onOpenHistory, showBuilding),
		[formatLocation, onOpenHistory, showBuilding]
	);
	const showPager = pages.length > 1 || totalReservations > initialPageSize;

	return (
		<Card size="small" className="shadow-sm">
			<Table<PendingCutRow>
				size="small"
				rowKey="key"
				sticky
				columns={columns}
				dataSource={rows}
				pagination={false}
				scroll={{ x: 1480 }}
				rowClassName={(row) => (row.kind === 'section' ? 'bg-gray-100' : '')}
				onRow={(row) =>
					row.kind === 'reservation'
						? ({
								'data-testid': `pending-cut-${row.reservation.id}`,
						  } as HTMLAttributes<HTMLElement>)
						: {}
				}
			/>
			{showPager && (
				<div className="flex justify-end pt-3">
					<Pagination
						current={safePage}
						pageSize={pageSize}
						total={pages.length * pageSize}
						showSizeChanger
						pageSizeOptions={pageSizeOptions}
						showTotal={() =>
							`${totalReservations.toLocaleString()} reservations`
						}
						onChange={(nextPage, nextSize) => {
							if (nextSize !== pageSize) {
								setPageSize(nextSize);
								setPage(1);
								return;
							}
							setPage(nextPage);
						}}
					/>
				</div>
			)}
		</Card>
	);
};

export default PendingCutsTable;
